import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchStaffUsers,
  createStaffUser,
  updateStaffUser,
  deleteStaffUser,
  StaffUserRecord,
  StaffRole,
  AdminUser,
} from '../../lib/api';
import {
  ShieldCheck,
  UserPlus,
  Shield,
  ScanLine,
  UserX,
  UserCheck,
  Trash2,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Mail,
  Crown,
  Printer,
  Activity,
} from 'lucide-react';

interface AdminStaffModuleProps {
  adminKey: string;
  currentUser?: AdminUser | null;
}

export const AdminStaffModule: React.FC<AdminStaffModuleProps> = ({ adminKey, currentUser }) => {
  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<StaffRole>('operations' as StaffRole);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isSuperAdmin = currentUser?.role === 'super_admin' || !currentUser;

  // Fetch staff users
  const {
    data,
    isLoading,
    refetch,
  } = useQuery<{ users: StaffUserRecord[] }>({
    queryKey: ['admin-staff-users', adminKey],
    queryFn: () => fetchStaffUsers(adminKey),
    enabled: !!adminKey,
  });

  const staffUsers = data?.users || [];

  // Create staff user mutation
  const createMutation = useMutation({
    mutationFn: (payload: { email: string; name?: string; role: StaffRole }) =>
      createStaffUser(adminKey, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff-users'] });
      setIsInviteModalOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('operations' as StaffRole);
      setActionMessage({
        type: 'success',
        text: `Team member ${res.user.email} successfully added with '${res.user.role}' permissions.`,
      });
      setTimeout(() => setActionMessage(null), 5000);
    },
    onError: (err: any) => {
      setActionMessage({
        type: 'error',
        text: err?.message || 'Failed to create team member.',
      });
    },
  });

  // Update staff user mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { role?: StaffRole; isActive?: boolean } }) =>
      updateStaffUser(adminKey, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff-users'] });
      setActionMessage({ type: 'success', text: 'Team member updated successfully.' });
      setTimeout(() => setActionMessage(null), 4000);
    },
    onError: (err: any) => {
      setActionMessage({ type: 'error', text: err?.message || 'Failed to update team member.' });
    },
  });

  // Delete staff user mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStaffUser(adminKey, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff-users'] });
      setActionMessage({ type: 'success', text: 'Staff account removed successfully.' });
      setTimeout(() => setActionMessage(null), 4000);
    },
    onError: (err: any) => {
      setActionMessage({ type: 'error', text: err?.message || 'Failed to delete staff member.' });
    },
  });

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    createMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim() || undefined,
      role: inviteRole,
    });
  };

  const getRoleBadge = (role: StaffRole | string) => {
    switch (role) {
      case 'super_admin':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-700 border border-amber-500/20">
            <Crown className="w-3.5 h-3.5 text-amber-600" />
            Super Admin
          </span>
        );
      case 'owner':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
            <Shield className="w-3.5 h-3.5 text-emerald-600" />
            Operations Owner
          </span>
        );
      case 'operations':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-teal-500/10 text-teal-700 border border-teal-500/20">
            <Activity className="w-3.5 h-3.5 text-teal-600" />
            Floor Operations Lead
          </span>
        );
      case 'desk':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-500/10 text-purple-700 border border-purple-500/20">
            <Printer className="w-3.5 h-3.5 text-purple-600" />
            Helpdesk &amp; Badge Desk
          </span>
        );
      case 'gate':
      case 'staff':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-sky-500/10 text-sky-700 border border-sky-500/20">
            <ScanLine className="w-3.5 h-3.5 text-sky-600" />
            Gate Turnstile Scanner
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] text-xs font-bold mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>5-Tier Role-Based Access Governance</span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">Team Accounts &amp; Operational Roles</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Manage administrative privileges across all 5 operational tiers: Super Admin, Owner, Floor Operations, Helpdesk &amp; Badge Desk, and Gate Turnstile Scanners.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition cursor-pointer"
            title="Refresh list"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setIsInviteModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0A3D27] text-white text-xs font-bold hover:bg-[#072d1c] transition shadow-sm cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
              <span>Invite Team Member</span>
            </button>
          )}
        </div>
      </div>

      {/* 5-Role Hierarchy Quick Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-amber-50/60 rounded-xl p-3.5 border border-amber-200/80 space-y-1">
          <div className="flex items-center gap-1.5 text-amber-800 font-bold text-xs uppercase tracking-wider">
            <Crown className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span>1. Super Admin</span>
          </div>
          <p className="text-[11px] text-amber-900/80 leading-relaxed">
            Full governance, staff invitations, role assignments, pricing &amp; financial recovery.
          </p>
        </div>

        <div className="bg-emerald-50/60 rounded-xl p-3.5 border border-emerald-200/80 space-y-1">
          <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs uppercase tracking-wider">
            <Shield className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span>2. Owner</span>
          </div>
          <p className="text-[11px] text-emerald-900/80 leading-relaxed">
            Commercial stall sales, CRM pipeline, speakers, broadcasts, and financial invoices.
          </p>
        </div>

        <div className="bg-teal-50/60 rounded-xl p-3.5 border border-teal-200/80 space-y-1">
          <div className="flex items-center gap-1.5 text-teal-800 font-bold text-xs uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5 text-teal-600 shrink-0" />
            <span>3. Operations</span>
          </div>
          <p className="text-[11px] text-teal-900/80 leading-relaxed">
            Event-day live ops, badge production, helpdesk, turnout analytics &amp; read-only sales.
          </p>
        </div>

        <div className="bg-purple-50/60 rounded-xl p-3.5 border border-purple-200/80 space-y-1">
          <div className="flex items-center gap-1.5 text-purple-800 font-bold text-xs uppercase tracking-wider">
            <Printer className="w-3.5 h-3.5 text-purple-600 shrink-0" />
            <span>4. Desk</span>
          </div>
          <p className="text-[11px] text-purple-900/80 leading-relaxed">
            Onsite registration helpdesk, badge printing, NFC linking &amp; perk counter support.
          </p>
        </div>

        <div className="bg-sky-50/60 rounded-xl p-3.5 border border-sky-200/80 space-y-1">
          <div className="flex items-center gap-1.5 text-sky-800 font-bold text-xs uppercase tracking-wider">
            <ScanLine className="w-3.5 h-3.5 text-sky-600 shrink-0" />
            <span>5. Gate</span>
          </div>
          <p className="text-[11px] text-sky-900/80 leading-relaxed">
            Dedicated turnstile QR badge scanning and admission verification at Pavilion 3 gates.
          </p>
        </div>
      </div>

      {/* Action Notification */}
      {actionMessage && (
        <div
          className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium ${
            actionMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {actionMessage.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
          )}
          <span>{actionMessage.text}</span>
        </div>
      )}

      {/* Staff Roster Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm">Active Staff Roster ({staffUsers.length})</h3>
          {!isSuperAdmin && (
            <span className="text-xs text-slate-400 italic">View-only mode (Super Admin role required to edit)</span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/75 border-b border-slate-100 text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">User</th>
                <th className="py-3 px-4">Operational Role</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Invited / Added</th>
                {isSuperAdmin && <th className="py-3 px-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 5 : 4} className="py-8 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-300" />
                    Loading staff members...
                  </td>
                </tr>
              ) : staffUsers.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 5 : 4} className="py-8 text-center text-slate-400">
                    No staff users registered yet.
                  </td>
                </tr>
              ) : (
                staffUsers.map((user) => {
                  const isCurrent = currentUser?.id === user.id || currentUser?.email === user.email;
                  const normalizedUserRole = (user.role as string) === 'staff' ? 'gate' : user.role;
                  return (
                    <tr key={user.id} className="hover:bg-slate-50/50 transition">
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-900">{user.name || 'Staff Member'}</div>
                        <div className="text-slate-500 flex items-center gap-1.5 mt-0.5">
                          <Mail className="w-3 h-3 text-slate-400" />
                          <span>{user.email}</span>
                          {isCurrent && (
                            <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold">
                              You
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-3.5 px-4">
                        {user.isActive ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            Deactivated
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-slate-500">
                        {new Date(user.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      {isSuperAdmin && (
                        <td className="py-3.5 px-4 text-right">
                          <div className="inline-flex items-center gap-2 justify-end">
                            {/* 5-Role selector dropdown */}
                            <select
                              value={normalizedUserRole}
                              disabled={isCurrent || updateMutation.isPending}
                              onChange={(e) =>
                                updateMutation.mutate({
                                  id: user.id,
                                  payload: { role: e.target.value as StaffRole },
                                })
                              }
                              className="px-2 py-1 text-[11px] font-medium border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0A3D27] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                              <option value="gate">Gate Turnstile Scanner (gate)</option>
                              <option value="desk">Helpdesk &amp; Badge Desk (desk)</option>
                              <option value="operations">Floor Operations Lead (operations)</option>
                              <option value="owner">Operations Owner (owner)</option>
                              <option value="super_admin">Super Admin (super_admin)</option>
                            </select>

                            {/* Active toggle */}
                            <button
                              disabled={isCurrent || updateMutation.isPending}
                              onClick={() =>
                                updateMutation.mutate({
                                  id: user.id,
                                  payload: { isActive: !user.isActive },
                                })
                              }
                              className={`p-1.5 rounded-lg border transition cursor-pointer ${
                                user.isActive
                                  ? 'border-slate-200 text-slate-600 hover:bg-slate-100'
                                  : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                              } disabled:opacity-30 disabled:cursor-not-allowed`}
                              title={user.isActive ? 'Deactivate account' : 'Reactivate account'}
                            >
                              {user.isActive ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                            </button>

                            {/* Delete account */}
                            <button
                              disabled={isCurrent || deleteMutation.isPending}
                              onClick={() => {
                                if (window.confirm(`Permanently remove ${user.email} from the staff directory?`)) {
                                  deleteMutation.mutate(user.id);
                                }
                              }}
                              className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                              title="Delete account"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-[#0A3D27]/10 text-[#0A3D27]">
                  <UserPlus className="w-5 h-5" />
                </div>
                <h3 className="font-black text-slate-900 text-lg">Invite Team Member</h3>
              </div>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold p-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]/20 focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Full Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sarah Sharma"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]/20 focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Permission Role (5 Operational Tiers) *
                </label>
                <div className="space-y-2">
                  {[
                    {
                      role: 'gate' as StaffRole,
                      label: 'Gate Turnstile Scanner (gate)',
                      desc: 'Check-in pass validation and turnstile QR scanning',
                    },
                    {
                      role: 'desk' as StaffRole,
                      label: 'Helpdesk & Badge Desk (desk)',
                      desc: 'Onsite registration helpdesk, badge re-printing & NFC linking',
                    },
                    {
                      role: 'operations' as StaffRole,
                      label: 'Floor Operations Lead (operations)',
                      desc: 'Full live operations, turnout analytics & read-only stall view',
                    },
                    {
                      role: 'owner' as StaffRole,
                      label: 'Operations Owner (owner)',
                      desc: 'Full commercial sales, CRM, speaker & attendee management',
                    },
                    {
                      role: 'super_admin' as StaffRole,
                      label: 'Super Admin (super_admin)',
                      desc: 'Full platform governance, pricing & user role permissions',
                    },
                  ].map((opt) => (
                    <label
                      key={opt.role}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                        inviteRole === opt.role
                          ? 'border-[#0A3D27] bg-[#0A3D27]/5'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="inviteRole"
                        value={opt.role}
                        checked={inviteRole === opt.role}
                        onChange={() => setInviteRole(opt.role)}
                        className="mt-0.5 text-[#0A3D27] focus:ring-[#0A3D27]"
                      />
                      <div>
                        <div className="text-xs font-bold text-slate-900">{opt.label}</div>
                        <div className="text-[11px] text-slate-500 leading-tight">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !inviteEmail.trim()}
                  className="px-5 py-2 rounded-xl text-xs font-bold bg-[#0A3D27] text-white hover:bg-[#072d1c] transition disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                >
                  {createMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Add Member</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
